import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useDispatch, useSelector } from 'react-redux';

import { getSmsDashboardConversationsKey } from './useConversations';
import { showResult } from '../utils';

import type {
  IConversation,
  IConversationsListPages,
} from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  onSuccess?: (data: any) => void;
}

interface FnProps {
  conversationId: number;
  name: string;
}

const renameConversationFn = async ({ conversationId, name }: FnProps) => {
  const API_CONVERSATION_NAME = `core/conversations/name`;

  const { data, error } = await dataService.updateOnly(API_CONVERSATION_NAME, {
    conversationId,
    name,
  });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useRenameConversation = (props: Props) => {
  const queryClient = useQueryClient();
  const { conversationType, filters, currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: renameConversationFn,
    onSuccess(data, variables) {
      const сonversationKey = getSmsDashboardConversationsKey({
        type: conversationType,
        read: filters.read,
        closed: filters.closed,
        lastMessageDate: filters.lastMessageDate,
        searchQuery: filters.searchQuery,
      });

      const conversations =
        queryClient.getQueryData<IConversationsListPages>(сonversationKey);

      const pages = structuredClone(conversations?.pages ?? []);
      const [firstPage, ...restPages] = pages;

      pages.forEach((page) => {
        const index = page.items.findIndex(
          (item: IConversation) => item.id === variables.conversationId,
        );
        if (index !== -1) {
          page.items[index] = {
            ...page.items[index],
            name: variables.name,
          };
        }
      });

      const updatedPages = [firstPage, ...restPages];

      queryClient.setQueryData<IConversationsListPages>(
        сonversationKey,
        (oldConversations) => {
          if (!oldConversations) return;
          return { ...oldConversations, pages: updatedPages };
        },
      );
      if (currentConversation.id === variables.conversationId) {
        changeCurrentConversation({
          ...currentConversation,
          name: variables.name,
        });
      }
      if (props.onSuccess) {
        props.onSuccess(data.data);
      }
    },
  });

  return {
    renameConversation: mutate,
    renameConversationLoading: isLoading,
    ...other,
  };
};
